import { motion } from 'framer-motion';
import { Calendar, Bell, Award, User, ArrowRight, FileText, BarChart3, PlusCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { UserRoles } from '@/contexts/auth/enums';
import Gradient from '@/assets/medihub_gradient_mesh.jpg';
import Gradient2 from '@/assets/medihub_gradient_mesh_turqoise.jpg';

const LoggedInHeroSection = () => {
  const { user } = useAuth();
  const firstName = user?.userProfile?.name?.firstName || '';
  const lastName = user?.userProfile?.name?.lastName || '';
  const fullName = `${lastName} ${firstName}`;
  const hasName = !!fullName;
  const navigate = useNavigate();

  // Check if user is an organizer
  const isOrganizer = user?.roles?.includes(UserRoles.ORGANIZER);

  return (
    <section className="pt-32 pb-16 px-4 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src={isOrganizer ? Gradient2 : Gradient}
          alt="MediHub gradient háttér"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 z-0"></div>
      </div>

      <div className="container mx-auto relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Welcome Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:col-span-3"
          >
            <Card className="bg-white/90 backdrop-blur-sm border-none shadow-xl overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <span className="text-sm font-medium text-medihub-accent bg-medihub-accent/10 py-1 px-3 rounded-full">
                      {isOrganizer ? 'Szervezői felület' : 'Személyre szabott események'}
                    </span>
                    <h1 className="text-3xl font-bold mt-3 mb-2">
                      {hasName ? `Üdvözöljük, ${fullName}!` : 'Üdvözöljük a MediHub-on!'}
                    </h1>
                    <p className="text-gray-600 max-w-2xl">
                      {isOrganizer
                        ? 'Kezelje rendezvényeit és követhesse nyomon az eseményeire való jelentkezéseket a MediHub rendszerében.'
                        : 'Fedezze fel a legújabb egészségügyi rendezvényeket és kreditpontos eseményeket, amelyek relevánsak az Ön szakterületén.'}
                    </p>
                  </div>
                  <Button asChild size="lg" className="bg-medihub-accent hover:bg-medihub-accent/90 mt-4 md:mt-0">
                    <Link to={isOrganizer ? "/organizer/dashboard" : "/calendar"} className="flex items-center gap-2">
                      {isOrganizer ? <BarChart3 className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
                      <span>{isOrganizer ? 'Szervezői vezérlőpult' : 'Események böngészése'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Role-specific action cards */}
          {isOrganizer ? (
            // Organizer-specific cards
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="col-span-1"
              >
                <Card className="h-full bg-white/70 backdrop-blur-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <PlusCircle className="h-10 w-10 text-medihub-primary mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Új esemény létrehozása</h3>
                    <p className="text-gray-700 mb-6 flex-grow">
                      Hozzon létre és tervezzen meg új szakmai eseményeket és konferenciákat.
                    </p>
                    <Button variant="outline" asChild className="w-full mt-auto border-medihub-primary/50 text-medihub-primary hover:bg-medihub-primary/10">
                      <Link to="/organizer/events/create">
                        Esemény létrehozása
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="col-span-1"
              >
                <Card className="h-full bg-white/70 backdrop-blur-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <FileText className="h-10 w-10 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Szervezői profil</h3>
                    <p className="text-gray-700 mb-6 flex-grow">
                      Frissítse szervezői adatokat és az eseményeinek megjelenését.
                    </p>
                    <Button variant="outline" asChild className="w-full mt-auto border-blue-400/50 text-blue-600 hover:bg-blue-100">
                      <Link to="/organizer/profile">
                        Profil megtekintése
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          ) : (
            // Professional-specific cards
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="col-span-1"
              >
                <Card className="h-full bg-white/70 backdrop-blur-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Award className="h-10 w-10 text-medihub-primary mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Kreditpontos események</h3>
                    <p className="text-gray-700 mb-6 flex-grow">
                      Böngésszen a kreditpontos események között, és gyűjtsön pontokat a szakmai fejlődéshez.
                    </p>
                    <Button variant="outline" asChild className="w-full mt-auto border-medihub-primary/50 text-medihub-primary hover:bg-medihub-primary/10">
                      <Link to="/calendar?creditPoints=true">
                        Kreditpontos események
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="col-span-1"
              >
                <Card className="h-full bg-white/70 backdrop-blur-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Bell className="h-10 w-10 text-medihub-accent mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Értesítések beállítása</h3>
                    <p className="text-gray-700 mb-6 flex-grow">
                      Állítsa be az értesítéseket, hogy ne maradjon le az Önt érdeklő eseményekről.
                    </p>
                    <Button
                      onClick={() => {
                        navigate('/participant/profile', { state: { defaultTab: 'ertesitesek', _ts: Date.now() } });

                      }}
                      variant="outline" className="w-full mt-auto border-medihub-accent/50 text-medihub-accent hover:bg-medihub-accent/10">
                      Értesítések kezelése
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="col-span-1"
              >
                <Card className="h-full bg-white/70 backdrop-blur-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <User className="h-10 w-10 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Profil szerkesztése</h3>
                    <p className="text-gray-700 mb-6 flex-grow">
                      Frissítse szakmai preferenciáit, hogy relevánsabb ajánlásokat kapjon.
                    </p>
                    <Button variant="outline" asChild className="w-full mt-auto border-blue-400/50 text-blue-600 hover:bg-blue-100">
                      <Link to="/participant/profile">
                        Profilom szerkesztése
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default LoggedInHeroSection;
